import { useState, useCallback, memo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import cn from 'clsx'
import { parse } from 'querystringify'

// Actions
import { actions as audioActions } from 'modules/audio/actions'

// Components
import { useMaxWidth, IconLoopSmall } from '@tracklib/kit'
import CoverArt from 'components/coverart'
import DisabledOverlay from 'components/track/disabledoverlay'
import Footer from 'components/track/footer'
import Header from 'components/track/header'
import LoopButton from 'components/track/loopbutton'
import PlayButton from 'components/playbutton'
import Waveform from 'components/track/waveform'

// Hooks
import { useProfile } from 'modules/profile/hooks'
import { useAudio, useAudioPlayStates } from 'modules/audio/hooks'
import { useIsFreeTrack } from 'modules/price/hooks'

// Selectors
import { songIsCountryRestricted } from 'modules/browse/selectors'
import { annotateTrackWithPrice } from 'modules/price/selectors'

// Types
import { Loop, State, DisplayMode } from 'types'

import styles from './track.css'

type TrackProps = {
  trackId: number
  songItemId: string
  loop?: Loop | null
  contextKey?: string
  renderSpecificTrackNames?: boolean
  renderOnlySpecificTrackName?: boolean
  mode?: DisplayMode
  mainClassName?: string
  forceHover?: boolean
  animatePlayButton?: boolean
  animateLoopButton?: boolean
  coverSizes?: string
}

const Track = ({
  trackId,
  songItemId,
  loop: initialLoop,
  contextKey = '',
  renderSpecificTrackNames,
  renderOnlySpecificTrackName,
  mode,
  mainClassName,
  forceHover,
  animatePlayButton,
  animateLoopButton,
  coverSizes,
}: TrackProps): JSX.Element => {
  const dispatch = useDispatch()
  const [isHovered, setIsHovered] = useState(false)
  const { isActive, isPlaying, isLoading, isLoadingLoop } = useAudioPlayStates(songItemId, contextKey)
  const { loop: currentLoop } = useAudio()
  const { hash, search } = useLocation()
  const state = useSelector<State, State>(s => s)
  const track = annotateTrackWithPrice(state, state.entities.tracks[String(trackId)])
  const song = state.entities.songs[String(track?.song)]
  const profile = useProfile()
  const isDisabled = songIsCountryRestricted(song, profile)
  const isXSmall = useMaxWidth(480)

  const loop = isActive ? currentLoop : initialLoop

  const handlePlay = useCallback(() => {
    if (!isLoading || !isLoadingLoop) {
      if (isActive && isPlaying) {
        dispatch(audioActions.togglePaused())
      } else if (trackId) {
        dispatch(audioActions.playTrack(songItemId, trackId, contextKey, true, loop))
      }
    }
  }, [contextKey, dispatch, isActive, isLoading, isLoadingLoop, isPlaying, loop, songItemId, trackId])

  const slug = track?.slug
  const getIsSelected = useCallback(() => {
    // Show selected state briefly when linking to track with hash
    if (!hash) {
      return false
    }
    const query = parse(search) as { modal: boolean }
    return hash ? hash.replace('#', '').split('?')[0] === slug && !query.modal : false
  }, [hash, search, slug])

  const modeClassName = cn(mode && styles[mode as keyof typeof styles], mode === 'extraCompact' && styles.compact)
  const isFreeTrack = useIsFreeTrack(trackId)

  if (!track || !song) {
    return <div className={cn(styles.wrapper, modeClassName)} />
  }

  const compact = mode === 'compact' || mode === 'extraCompact'

  return (
    <div
      className={cn(styles.wrapper, modeClassName, getIsSelected() && styles.isSelected)}
      id={renderSpecificTrackNames ? track.slug : ''}
      datatesting="track"
    >
      <div
        aria-label="Play song"
        data-play="true"
        datatesting="play_track"
        className={cn(
          styles.hoverButton,
          isActive && styles.isActive,
          compact && styles.compact,
          mode === 'extraCompact' && styles.extraCompact,
          isFreeTrack && styles.isFreeTrack,
        )}
        onClick={handlePlay}
        role="button"
        tabIndex={0}
        {...(isXSmall
          ? {}
          : {
              onMouseEnter: () => setIsHovered(true),
              onMouseLeave: () => setIsHovered(false),
            })}
      />
      <div className={styles.rowActive} />
      <CoverArt cover={song.cover} className={styles.cover} compact={compact} sizes={coverSizes}>
        {loop && !isActive && (
          <div className={cn(styles.loopIcon, compact && styles.loopIconCompact)}>
            <IconLoopSmall />
          </div>
        )}
        <div
          className={cn(
            styles.coverButtons,
            (isHovered || forceHover) && styles.isHovered,
            (isLoading || isLoadingLoop) && styles.isLoading,
            isPlaying && styles.isPlaying,
            isActive && styles.isActive,
          )}
        >
          <PlayButton
            entity="track"
            value={{ songItemId, trackId, loop: loop as Loop | undefined }}
            datatesting="songitemplaybutton"
            contextKey={contextKey}
            compact={compact}
            animated={animatePlayButton}
          />
          <LoopButton loop={loop} isActive={isActive} isLoadingLoop={isLoadingLoop} compact={compact} animated={animateLoopButton} />
        </div>
      </CoverArt>
      <div className={cn(styles.main, mainClassName)}>
        <Header
          song={song}
          track={track}
          loop={loop}
          hideButtons={isDisabled}
          renderSpecificTrackNames={renderSpecificTrackNames}
          renderOnlySpecificTrackName={renderOnlySpecificTrackName}
          mode={mode}
        >
          {compact && (
            <Waveform
              track={track}
              loop={loop}
              contextKey={contextKey}
              songItemId={songItemId}
              isHovered={isHovered}
              className={styles.waveformCompact}
            />
          )}
        </Header>
        {!compact && (
          <>
            <div className={styles.waveformWrapper}>
              {/* TODO(Kitti): Samuel hjälp!! */}
              <Waveform
                track={track}
                loop={loop}
                contextKey={contextKey}
                songItemId={songItemId}
                isHovered={isHovered}
                className={styles.waveform}
              />
            </div>
            <Footer song={song} track={track} />
          </>
        )}
      </div>
      {isDisabled && <DisabledOverlay className={cn(styles.disabledOverlayTracks)} />}
    </div>
  )
}

export default memo<TrackProps>(Track)
